import { Grid, Button } from '@mui/material'
import {
    CoreAlert,
    CoreTypography,
    useTheme
} from '@thriveglobal/thrive-web-leafkit'
import MicrosoftButton from '../../components/elements/MicrosoftButton'
import { FormattedMessage } from 'react-intl'

export type signInButtonOption = 'microsoft' | 'sso' | 'magicLink'

export interface SignInButtonOptionsProps {
    buttonsToShow: Array<signInButtonOption>
    signInWithMicrosoft: () => void
    signInWithSSO: () => void
    signInWithMagicLink?: () => void
    magicLinkSent?: boolean
    showMagicLinkError?: boolean
}

const LandingPageSignInOptions: React.FC<SignInButtonOptionsProps> = ({
    buttonsToShow,
    signInWithMicrosoft,
    signInWithSSO,
    signInWithMagicLink,
    magicLinkSent,
    showMagicLinkError
}) => {
    const theme = useTheme()
    return (
        <>
            {buttonsToShow.map((buttonToShow, index) => {
                const isLastIndex = index === buttonsToShow.length - 1
                if (buttonToShow === 'microsoft') {
                    return (
                        <MicrosoftButton
                            key={buttonToShow}
                            sx={{ ...(!isLastIndex && { marginBottom: 2 }) }}
                            variant="outlined"
                            color="info"
                            signInWithMicrosoft={signInWithMicrosoft}
                        />
                    )
                }
                if (buttonToShow === 'sso') {
                    return (
                        <Grid
                            key={buttonToShow}
                            item
                            sx={
                                buttonsToShow.length > 1 && {
                                    marginRight: 'auto'
                                }
                            }
                        >
                            <Button
                                size="large"
                                variant="outlined"
                                color="info"
                                type="button"
                                onClick={signInWithSSO}
                                sx={{
                                    width: '36rem',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    ...(!isLastIndex && { marginBottom: 2 }),
                                    [theme.breakpoints.down('sm')]: {
                                        width: '100%'
                                    }
                                }}
                            >
                                <CoreTypography customVariant="buttonLarge">
                                    <FormattedMessage
                                        description="Label for button prompting the user to sign in with SSO"
                                        defaultMessage="Sign in with SSO"
                                    />
                                </CoreTypography>
                            </Button>
                        </Grid>
                    )
                }
                if (buttonToShow === 'magicLink') {
                    return (
                        <Grid
                            key={buttonToShow}
                            item
                            sx={
                                buttonsToShow.length > 1 && {
                                    marginRight: 'auto'
                                }
                            }
                        >
                            {showMagicLinkError && (
                                <CoreAlert
                                    severity="error"
                                    sx={{
                                        marginBottom: 2
                                    }}
                                >
                                    <FormattedMessage
                                        defaultMessage="There was a problem sending your link to sign in"
                                        description="Message shown when we were not able to send the user a magic link"
                                    />
                                </CoreAlert>
                            )}
                            <Button
                                size="large"
                                variant="outlined"
                                color="info"
                                type="button"
                                onClick={signInWithMagicLink}
                                sx={{
                                    width: '36rem',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    ...(!isLastIndex && { marginBottom: 2 }),
                                    [theme.breakpoints.down('sm')]: {
                                        width: '100%'
                                    }
                                }}
                            >
                                {!magicLinkSent && (
                                    <CoreTypography customVariant="buttonLarge">
                                        <FormattedMessage
                                            description="Label for button prompting the user to sign in with link sent to their email"
                                            defaultMessage="Send me a link to sign in"
                                        />
                                    </CoreTypography>
                                )}
                                {magicLinkSent && (
                                    <CoreTypography customVariant="buttonLarge">
                                        <FormattedMessage
                                            description="Label for button prompting the user to send sign in link again"
                                            defaultMessage="Resend link"
                                        />
                                    </CoreTypography>
                                )}
                            </Button>
                            {magicLinkSent && (
                                <CoreTypography>
                                    <FormattedMessage
                                        defaultMessage="Didn't get your email? Check your spam folder"
                                        description="Message shown to users to let them know to check their spam for the email"
                                    />
                                </CoreTypography>
                            )}
                        </Grid>
                    )
                }
                return null
            })}
        </>
    )
}

export default LandingPageSignInOptions
